import { Theme } from "@amx/ux-twoui-theming";
import { FontWeights, IButtonStyles, IModalStyleProps, IModalStyles, IStackStyles, IStyleFunctionOrObject, mergeStyles, mergeStyleSets } from "@fluentui/react";
import { heightMedia, media, viewportWidthMin } from "../../styles/media";
import { getFullScreenStyles } from "../DialogV2/DialogV2.styles";
import {
    DialogBodyStyle,
    DialogBodyWidth,
    DialogBorderRadius,
    DialogFooterStyle,
    DialogHeaderStyle,
    FullScreenModeProps,
    StickyHeaderFooterProps,
    ViewportOrPercentageBasedHeight
} from "./Dialog.types";

export const defaultDialogBodyStyle: DialogBodyStyle = { padding: "24px", smallScreenPadding: "16px", removeBodyBottomPadding: false };
export const defaultDialogHeaderStyle: DialogHeaderStyle = { headerLeftRightPadding: "24px", withHeaderHorizontalLine: false };
export const defaultDialogFooterStyle: DialogFooterStyle = { isStickyFooterWithText: false, hasFooterBackground: false, hasFooterHorizontalLine: false };

export const getMainContentStyles = (theme: Theme) => mergeStyles({

    backgroundColor: theme.palette.neutralHigh
});

export const getSubtextContentStyles = () => mergeStyles({
    marginBottom: "24px",
    selectors: {
        [media.maxS]: {
            marginBottom: "16px",
        },
    }
}
);

export const getHeaderContentStyles = (theme: Theme, dialogHeaderStyle = defaultDialogHeaderStyle) => mergeStyles([
    theme.fonts?.xLarge,
    {
        color: theme.semanticColors.bodyText,
        fontWeight: FontWeights.semibold,
        padding: dialogHeaderStyle.withHeaderHorizontalLine ? `16px ${dialogHeaderStyle.headerLeftRightPadding} 16px` : `16px ${dialogHeaderStyle.headerLeftRightPadding} 0`,
        borderBottom: dialogHeaderStyle.withHeaderHorizontalLine ? `1px solid ${theme.palette.neutralLighterAlt}` : undefined,
        selectors: {
            [media.maxS]: {
                padding: "16px",
            },
        },
    },

]);

export const getBodyContentStyles = (dialogBodyStyle = defaultDialogBodyStyle) => mergeStyles({
    padding: dialogBodyStyle.padding,
    paddingBottom: dialogBodyStyle.removeBodyBottomPadding ? 0 : undefined,
    selectors: {
        [media.maxS]: {
            padding: dialogBodyStyle.smallScreenPadding,
            paddingBottom: dialogBodyStyle.removeBodyBottomPadding ? 0 : undefined
        },
    }
});

export const getFooterContentStyles = (theme: Theme, dialogFooterStyle = defaultDialogFooterStyle) => mergeStyleSets({
    footer: {
        padding: "0 24px 24px 24px",
        borderTop: dialogFooterStyle.isStickyFooterWithText || dialogFooterStyle.hasFooterHorizontalLine ? `1px solid ${theme.palette.neutralLighterAlt}` : "none",
        backgroundColor: dialogFooterStyle.isStickyFooterWithText || dialogFooterStyle.hasFooterBackground ? theme.palette.neutralMed : "transparent",
        selectors: {
            [media.maxS]: {
                padding: "0 16px 16px 16px",
            },
        }
    }
});

export const getIconButtonStyles = (theme: Theme, onLeftSide: boolean = false): IButtonStyles => ({
    root: {
        color: theme.palette.neutralPrimary,
        // We need to offset the margin for icon space to match the design
        marginLeft: onLeftSide ? "-8px" : "auto",
        marginRight: onLeftSide ? "auto" : "-8px"
    },
    rootHovered: {
        color: theme.palette.neutralDark,
    },
});

export const getIconPlaceholderStyles = (): React.CSSProperties => ({
    width: "24px",  // 32px (icon button width) - 8px (offset set above in getIconButtonStyles)
    flexShrink: 0
});

export const progressBarStyles = {
    itemProgress: {
        padding: 0
    }
};

// Class name for dialog body content area to be used by Fluent Modal firstFocusableSelector
export const dialogBodySelector = "dialogBody";

export const getScrollableBodyStyles = (stickyProps?: StickyHeaderFooterProps, minBodyHeight?: ViewportOrPercentageBasedHeight): IStackStyles => {
    if (!stickyProps) {
        return {
            root: {
                height: "100%"
            }
        };
    }

    // By default the max height is 60vh. If the consumer sets minBodyHeight to be larger than that,
    // it will cause design inconsistencies. Here we set the default max height to auto. If desired,
    // the consumer can override this by setting stickyHeaderFooter.bodyMaxHeight.
    const defaultMaxHeight = minBodyHeight ? "auto" : "60vh";

    return {
        root: {
            [stickyProps.forceStickyOnSmallScreens ? heightMedia.minS : heightMedia.minM]: {
                maxHeight: stickyProps.bodyMaxHeight ?? defaultMaxHeight,
                minHeight: stickyProps.bodyMinHeight ?? "auto",
                height: "100%",
                overflowY: "auto",
                outline: "none"
            }
        }
    };
};

export const getStickyHeaderFooterModalStyles = (stickyProps: StickyHeaderFooterProps) =>
    mergeStyleSets<Partial<IModalStyles>>({
        main: {
            [stickyProps.forceStickyOnSmallScreens ? heightMedia.minS : heightMedia.minM]: {
                display: "flex",
                flexDirection: "column",
                overflowY: "hidden"
            }
        }
    });

export const getStyleForModalProps = (
    theme: Theme,
    minBodyHeight?: ViewportOrPercentageBasedHeight,
    staticBodyWidth?: DialogBodyWidth,
    styles?: IStyleFunctionOrObject<IModalStyleProps, IModalStyles>,
    borderRadius?: DialogBorderRadius,
    fullScreenMode?: FullScreenModeProps
): IStyleFunctionOrObject<IModalStyleProps, IModalStyles> => {
    const dialogBodyMinHeight = minBodyHeight ? `${minBodyHeight.height}${minBodyHeight.unit}` : undefined;

    // tslint:disable: no-magic-numbers
    const staticBodyWidthAsNumber = staticBodyWidth ? parseInt(staticBodyWidth, 10) : 0;
    const toMerge: IModalStyles = {
        main: {
            backgroundColor: theme.palette.neutralHigh,
            borderRadius: borderRadius,
            maxWidth: staticBodyWidth || "480px",
            minWidth: staticBodyWidth || "auto",
            height: dialogBodyMinHeight,
            top: 0,
            [media.maxL]: {
                maxWidth: staticBodyWidth ? (viewportWidthMin.l > staticBodyWidthAsNumber ? staticBodyWidth : "96%") : "480px",
                minWidth: staticBodyWidth ? (viewportWidthMin.l > staticBodyWidthAsNumber ? staticBodyWidth : "96%") : "auto",
            },
            [media.maxM]: {
                margin: "16px",
                // When staticBodyWidth is set, and the viewport hasn't grown enough yet, we allow the
                // width to build up to the static max instead of accidentally being too wide.
                // staticBodyWidth will always be greater than the viewport
                maxWidth: staticBodyWidth ? (viewportWidthMin.m > staticBodyWidthAsNumber ? staticBodyWidth : "96%") : "96%",
                minWidth: staticBodyWidth ? (viewportWidthMin.m > staticBodyWidthAsNumber ? staticBodyWidth : "96%") : "auto",
                // When minBodyHeight is set, and the viewport has not grown past the medium size, we
                // "lock" the minHeight to 96% as defined in the Dialog component spec.
                minHeight: minBodyHeight ? "92%" : undefined
            },
            [media.maxS]: {
                margin: "8px",
                // staticBodyWidth will always be greater than the viewport
                maxWidth: "96%",
                minWidth: "96%",
                minHeight: minBodyHeight ? "96%" : undefined
            }
        },
        scrollableContent: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
            height: "100%",
            minWidth: "288px",
            position: "relative"
        },
        root: undefined,
        layer: undefined,
        keyboardMoveIconContainer: undefined,
        keyboardMoveIcon: undefined
    };

    const fullScreenStyles = fullScreenMode ? getFullScreenStyles(fullScreenMode) : undefined;

    return mergeStyleSets(toMerge, styles, fullScreenStyles);
};
